import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, MessageCircle, ArrowRight, Sun, Droplets, Layers, LineChart, Factory } from "lucide-react";
import { buildWhatsAppUrl } from "../../lib/constants";
import { http } from "../../lib/api";
import { trackEvent, gtmPush } from "../../lib/api";
import { getSessionId, getTrackingPayload } from "../../lib/utm";

const PROJECT_TYPES = [
  { key: "malla_sombra", label: "Malla sombra", icon: Sun },
  { key: "membrana_pvc", label: "Membrana PVC", icon: Droplets },
  { key: "tensoestructura_arquitectonica", label: "Tensoestructura arquitectónica", icon: Layers },
  { key: "techo_canchas", label: "Techo para canchas", icon: LineChart },
  { key: "cubierta_industrial", label: "Cubierta industrial", icon: Factory },
];

const CITIES = [
  "Monterrey", "CDMX", "Guadalajara", "Queretaro", "Puebla",
  "Cancun", "Merida", "Tijuana", "otra",
];
const CITY_LABELS = {
  Monterrey: "Monterrey", CDMX: "CDMX", Guadalajara: "Guadalajara",
  Queretaro: "Querétaro", Puebla: "Puebla", Cancun: "Cancún",
  Merida: "Mérida", Tijuana: "Tijuana", otra: "Otra ciudad",
};

const PLANS = [
  { v: "si", l: "Sí, tengo planos" },
  { v: "en_proceso", l: "En proceso" },
  { v: "aun_no", l: "Aún no" },
];

// Map calculator project type keys → QuoteForm project_type values
const PROJECT_MAP_TO_FORM = {
  malla_sombra: "malla_sombra",
  membrana_pvc: "membrana_pvc",
  tensoestructura_arquitectonica: "tensoestructura_arquitectonica",
  techo_canchas: "techo_canchas",
  cubierta_industrial: "cubierta_industrial",
};

// Map sqm (number) → QuoteForm select value
function sqmToFormRange(sqm) {
  if (sqm < 100) return "<100";
  if (sqm < 300) return "100-300";
  if (sqm < 600) return "300-600";
  if (sqm <= 1200) return "600-1200";
  return ">1200";
}

// Map calculator city → QuoteForm city label
const CITY_MAP_TO_FORM = {
  Monterrey: "Monterrey", CDMX: "CDMX", Guadalajara: "Guadalajara",
  Queretaro: "Querétaro", Puebla: "Puebla", Cancun: "Cancún",
  Merida: "Mérida", Tijuana: "Tijuana", otra: "Otra ciudad",
};

function useAnimatedNumber(value, duration = 500) {
  const [display, setDisplay] = useState(value);
  const ref = useRef(value);
  useEffect(() => {
    const from = ref.current;
    const to = value;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      const v = Math.round(from + (to - from) * eased);
      setDisplay(v);
      if (p < 1) raf = requestAnimationFrame(tick);
      else { ref.current = to; setDisplay(to); }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return display;
}

export default function BudgetCalculator() {
  const [projectType, setProjectType] = useState("tensoestructura_arquitectonica");
  const [sqm, setSqm] = useState(250);
  const [city, setCity] = useState("Monterrey");
  const [hasPlans, setHasPlans] = useState("si");
  const [complexity, setComplexity] = useState("estandar");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef();

  const payload = useMemo(() => {
    const base = {
      project_type: projectType,
      square_meters: Number(sqm) || 0,
      city,
      has_plans: hasPlans,
      session_id: getSessionId(),
      ...getTrackingPayload(),
    };
    if (projectType === "tensoestructura_arquitectonica") base.complexity = complexity;
    return base;
  }, [projectType, sqm, city, hasPlans, complexity]);

  useEffect(() => {
    if (!payload.square_meters || payload.square_meters < 1) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await http.post("/calculator/estimate", payload);
        setResult(data);
        gtmPush("calculator_estimate", {
          product_type: payload.project_type,
          square_meters: payload.square_meters,
          city: payload.city,
          tier: data?.tier,
          estimated_min: data?.min_mxn,
          estimated_max: data?.max_mxn,
        });
      } catch (_) {
        setResult(null);
      } finally {
        setLoading(false);
      }
    }, 320);
    return () => clearTimeout(debounceRef.current);
  }, [payload]);

  const minMxn = useAnimatedNumber(result?.min_mxn || 0);
  const maxMxn = useAnimatedNumber(result?.max_mxn || 0);

  const prefillAndScroll = () => {
    trackEvent("calculator_to_quote", {
      project_type: projectType, sqm, city, min: result?.min_mxn, max: result?.max_mxn,
    });
    window.dispatchEvent(new CustomEvent("vt:prefillQuote", {
      detail: {
        typeKey: PROJECT_MAP_TO_FORM[projectType],
        squareMeters: sqmToFormRange(sqm),
        city: CITY_MAP_TO_FORM[city],
        hasPlans: hasPlans === "en_proceso" ? "proceso" : hasPlans,
      },
    }));
    const el = document.getElementById("cotiza");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const fmt = (n) => `$${(n || 0).toLocaleString("es-MX")}`;

  return (
    <section
      id="calculadora"
      data-testid="budget-calculator"
      className="relative text-white section-pad bg-navy-500"
    >
      <div className="absolute inset-0 bg-grain pointer-events-none opacity-50" />
      <div className="container-tight relative">
        <div className="flex items-center gap-3 mb-4">
          <span className="divider-accent" />
          <span className="kicker-bright flex items-center gap-2">
            <Sparkles className="w-3 h-3" /> Calculadora · Estimación automática
          </span>
        </div>
        <h2 className="font-display font-black tracking-tightest text-4xl md:text-5xl lg:text-6xl leading-[0.95] max-w-4xl">
          Calcula tu <span className="text-accent">presupuesto</span> en 10 segundos.
        </h2>
        <p className="mt-6 max-w-2xl text-white/70 text-base md:text-lg">
          Una estimación inicial basada en los precios promedio de 2026. La cotización final la damos tras visita técnica.
        </p>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-8 lg:gap-12">
          {/* LEFT: Inputs */}
          <div className="space-y-8">
            {/* Project type cards */}
            <div>
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/60">Tipo de proyecto</span>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="calc-project-types">
                {PROJECT_TYPES.map((p) => {
                  const Icon = p.icon;
                  const active = projectType === p.key;
                  return (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => setProjectType(p.key)}
                      className={`text-left p-4 border transition-colors ${active ? "bg-accent text-white border-accent" : "bg-eclipse-900 border-white/10 text-white hover:border-accent"}`}
                      data-testid={`calc-type-${p.key}`}
                    >
                      <Icon className="w-5 h-5 mb-2" />
                      <p className="font-display font-bold uppercase text-xs tracking-tight leading-tight">{p.label}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* m² slider */}
            <div>
              <div className="flex items-center justify-between">
                <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/60">Metros cuadrados</span>
                <input
                  type="number"
                  min={1}
                  max={10000}
                  value={sqm}
                  onChange={(e) => setSqm(Math.max(1, Math.min(10000, parseInt(e.target.value) || 0)))}
                  className="w-24 bg-eclipse-900 border border-white/15 px-3 py-1.5 font-mono text-right text-white text-sm focus:outline-none focus:border-accent"
                  data-testid="calc-sqm-input"
                />
              </div>
              <input
                type="range"
                min={10}
                max={3000}
                step={10}
                value={Math.min(sqm, 3000)}
                onChange={(e) => setSqm(Number(e.target.value))}
                className="mt-4 w-full accent-[#8cb62c]"
                data-testid="calc-sqm-slider"
              />
              <div className="flex justify-between text-[10px] font-mono text-white/40 uppercase tracking-widest mt-2">
                <span>10 m²</span><span>3,000 m²</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* City */}
              <div>
                <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/60">Ciudad</span>
                <select
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="mt-3 w-full bg-eclipse-900 border border-white/15 px-4 py-3 text-white focus:outline-none focus:border-accent"
                  data-testid="calc-city"
                >
                  {CITIES.map((c) => <option key={c} value={c}>{CITY_LABELS[c]}</option>)}
                </select>
              </div>
              {/* Plans */}
              <div>
                <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/60">¿Tienes planos?</span>
                <div className="mt-3 flex gap-2 flex-wrap" data-testid="calc-plans">
                  {PLANS.map((p) => (
                    <button
                      key={p.v}
                      type="button"
                      onClick={() => setHasPlans(p.v)}
                      className={`px-3 py-2 font-mono text-[10px] uppercase tracking-wider border transition-colors ${hasPlans === p.v ? "bg-accent text-white border-accent" : "border-white/20 text-white/80 hover:border-accent"}`}
                      data-testid={`calc-plans-${p.v}`}
                    >
                      {p.l}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Complexity (only tensoestructura) */}
            {projectType === "tensoestructura_arquitectonica" && (
              <div data-testid="calc-complexity-block">
                <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/60">Complejidad estructural</span>
                <div className="mt-3 flex gap-2">
                  {[{ v: "estandar", l: "Estándar" }, { v: "compleja", l: "Compleja (+25%)" }].map((o) => (
                    <button
                      key={o.v}
                      type="button"
                      onClick={() => setComplexity(o.v)}
                      className={`px-4 py-2 font-mono text-[10px] uppercase tracking-wider border transition-colors ${complexity === o.v ? "bg-accent text-white border-accent" : "border-white/20 text-white/80 hover:border-accent"}`}
                      data-testid={`calc-complexity-${o.v}`}
                    >
                      {o.l}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT: Result card */}
          <motion.aside
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            className="lg:sticky lg:top-24 self-start bg-eclipse-900 border border-accent/40 p-6 md:p-8"
            data-testid="calc-result-card"
          >
            {result?.quote_on_request ? (
              <div className="text-center py-6" data-testid="calc-quote-on-request">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent">Cotización bajo consulta</p>
                <h3 className="mt-3 font-display font-black text-2xl md:text-3xl">
                  Para <span className="text-accent">{CITY_LABELS[city]}</span> preferimos cotizar <br/>de forma <span className="text-accent">personalizada</span>.
                </h3>
                <p className="mt-4 text-white/65 text-sm">Contáctanos y respondemos en menos de 24 h hábiles.</p>
                <a
                  href={buildWhatsAppUrl(`Hola, quiero cotizar un proyecto (${sqm} m², ciudad: ${CITY_LABELS[city]}).`)}
                  target="_blank" rel="noopener noreferrer"
                  onClick={() => trackEvent("calc_whatsapp_click_other_city")}
                  className="mt-6 inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#25D366] text-white font-display font-bold uppercase tracking-wider text-sm hover:brightness-110 transition"
                  data-testid="calc-whatsapp-other"
                >
                  <MessageCircle className="w-5 h-5" /> Contactar por WhatsApp
                </a>
              </div>
            ) : (
              <>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">Rango estimado</p>
                <div className="mt-3 font-display font-black tracking-tightest leading-none" data-testid="calc-range">
                  <div className="text-2xl md:text-3xl text-white/85">{fmt(minMxn)}</div>
                  <div className="text-[11px] font-mono uppercase tracking-widest text-white/40 my-2">a</div>
                  <div className="text-3xl md:text-4xl text-accent">{fmt(maxMxn)}</div>
                  <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-white/50">MXN · {result?.project_type_label}</div>
                </div>

                <div className="mt-6 border-t border-white/10 pt-6">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/50">Precio por m²</p>
                  <p className="mt-2 font-display font-bold text-lg text-white">
                    {fmt(result?.price_per_sqm_min)} — {fmt(result?.price_per_sqm_max)} <span className="text-white/50 text-sm font-normal">MXN/m²</span>
                  </p>
                </div>

                {result?.notes && result.notes.length > 0 && (
                  <ul className="mt-6 space-y-2 border-t border-white/10 pt-6" data-testid="calc-notes">
                    {result.notes.map((n, i) => (
                      <li key={i} className="text-[12px] text-white/75 flex items-start gap-2">
                        <span className="text-accent">·</span><span>{n}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Tier block */}
                {result?.tier && (
                  <TierBlock
                    tier={result.tier}
                    label={result.tier_label}
                    message={result.tier_message}
                    primaryCta={result.primary_cta}
                    onForm={prefillAndScroll}
                    waMessage={
                      result.tier === "small"
                        ? `Hola, calculé un proyecto de ${result.project_type_label} de ${sqm} m² en ${CITY_LABELS[city]}. Quiero atención express.`
                        : `Hola, calculé un proyecto de ${result.project_type_label} (${sqm} m², ${CITY_LABELS[city]}). Quiero hablar con un ingeniero.`
                    }
                  />
                )}

                {result?.promotion_message && (
                  <div className="mt-5 border border-accent/40 bg-accent/10 px-4 py-3 text-[12px] text-accent/95 leading-relaxed" data-testid="calc-promotion">
                    {result.promotion_message}
                  </div>
                )}

                <p className="mt-5 text-[11px] text-white/45 italic leading-relaxed">
                  {result?.disclaimer || "Rango estimado automático. Cotización final tras visita técnica."}
                </p>
              </>
            )}
          </motion.aside>
        </div>
      </div>
    </section>
  );
}


function TierBlock({ tier, label, message, primaryCta, onForm, waMessage }) {
  const wa = buildWhatsAppUrl(waMessage);
  const tone =
    tier === "small"
      ? { ring: "border-white/15", chip: "border-accent/60 text-accent bg-transparent" }
      : tier === "medium"
      ? { ring: "border-blue-400/30", chip: "border-blue-400/60 text-blue-300 bg-blue-500/5" }
      : { ring: "border-accent/60", chip: "border-accent text-accent bg-accent/10" };

  return (
    <div className={`mt-7 border ${tone.ring} bg-eclipse-900/60 p-5`} data-testid={`calc-tier-${tier}`}>
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 border ${tone.chip} font-mono text-[10px] uppercase tracking-widest`}>
        {tier === "premium" ? <span aria-hidden>🏆</span> : null}
        {label}
      </span>
      <p className="mt-3 text-white/85 text-sm leading-relaxed">{message}</p>

      {tier === "premium" && (
        <ul className="mt-4 space-y-1.5" data-testid="calc-premium-bullets">
          {[
            "Memoria de cálculo certificada incluida",
            "Simulación de túnel de viento sin costo",
            "Ingeniero senior dedicado a tu proyecto",
          ].map((b) => (
            <li key={b} className="text-[12px] text-white/75 flex items-start gap-2">
              <span className="text-accent font-bold">✓</span><span>{b}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-5 flex flex-col gap-2.5">
        {primaryCta === "whatsapp" ? (
          <>
            <a
              href={wa} target="_blank" rel="noopener noreferrer"
              onClick={() => trackEvent("calc_tier_primary", { tier, cta: "whatsapp" })}
              className="inline-flex items-center justify-center gap-2 px-6 py-4 bg-[#25D366] text-white font-display font-bold uppercase tracking-wider text-xs hover:brightness-110 transition"
              data-testid="calc-tier-primary-wa"
            >
              <MessageCircle className="w-4 h-4" /> Hablar por WhatsApp ahora
              <ArrowRight className="w-4 h-4" />
            </a>
          </>
        ) : (
          <>
            <button
              type="button" onClick={onForm}
              className="btn-accent justify-center group"
              data-testid="calc-tier-primary-form"
            >
              {tier === "premium" ? "Solicitar cotización formal" : "Agendar llamada de 20 min"}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
            <a
              href={wa} target="_blank" rel="noopener noreferrer"
              onClick={() => trackEvent("calc_tier_secondary", { tier, cta: "whatsapp" })}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#25D366] text-white font-display font-bold uppercase tracking-wider text-[11px] hover:brightness-110 transition"
              data-testid="calc-tier-secondary-wa"
            >
              <MessageCircle className="w-4 h-4" /> O escríbenos por WhatsApp
            </a>
          </>
        )}
      </div>
    </div>
  );
}
